import React, { useState, useCallback, useRef, useMemo, type ReactNode } from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import Link from '@atlaskit/link';
import Button from '@atlaskit/button';
import EditorPanelIcon from '@atlaskit/icon/core/migration/information--editor-panel';
import { Box, xcss } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { FormattedMessage } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { IPHLink } from '../../iph-link/index.tsx';

export type Props = {
	buttonLabel: MessageDescriptor;
	description: MessageDescriptor;
	linkToDocumentationText?: MessageDescriptor;
	/** article id link for in product help.
	 * It will use the in product help component HelpPanelLink if you provide an article id
	 * otherwise it will use an <a> element and open the link in the new browser tab when click
	 */
	linkToInProductHelpArticleId?: string;
	/** use as the fallback html link */
	itemDocumentationUrl?: string;
	whatsThisLabelIdRef?: string;
};

export const WhatIsThisWithInProductHelp = (props: Props) => {
	const [isOpen, setIsOpen] = useState(false);

	const onClose = useCallback(() => setIsOpen(false), [setIsOpen]);

	const buttonRef = useRef<HTMLButtonElement>(null);

	const buttonId = `${props.whatsThisLabelIdRef}-what-is-this-button`;

	const onToggle: () => void = useCallback(() => {
		// we need to focus the button so that pressing ESC will close the popup
		if (buttonRef.current !== undefined && buttonRef.current !== null) {
			buttonRef.current.focus();
		}

		setIsOpen(!isOpen);
	}, [buttonRef, setIsOpen, isOpen]);

	const { formatMessage } = useIntl();

	const {
		itemDocumentationUrl,
		linkToDocumentationText,
		linkToInProductHelpArticleId,
		description,
		buttonLabel,
	} = props;

	const isInValidLink = (url: undefined | string) => url === undefined || url == null || url === '';

	const linkToItemDocumentation = useMemo(() => {
		if (isInValidLink(itemDocumentationUrl) || linkToDocumentationText === undefined) {
			return null;
		}

		const documentationLink = fg('replace-anchor-with-link__morphinecake') ? (
			<Link href={itemDocumentationUrl || ''} target="_blank" rel="noopener noreferrer">
				{formatMessage(linkToDocumentationText)}
			</Link>
		) : (
			// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
			<a href={itemDocumentationUrl} target="_blank" rel="noopener noreferrer">
				{formatMessage(linkToDocumentationText)}
			</a>
		);

		if (linkToInProductHelpArticleId === undefined) {
			return documentationLink;
		}

		return (
			<IPHLink
				articleId={linkToInProductHelpArticleId}
				linkText={linkToDocumentationText}
				documentationUrl={itemDocumentationUrl}
			/>
		);
	}, [itemDocumentationUrl, linkToDocumentationText, linkToInProductHelpArticleId, formatMessage]);

	return (
		<Container>
			<Popup
				isOpen={isOpen}
				onClose={onClose}
				placement="right"
				messageId="project-settings-apps-issue-type-page-product-features.common.ui.field-category.what-is-this-with-in-product-help.popup"
				messageType="transactional"
				shouldRenderToParent
				content={() => (
					<Box xcss={contentStyles}>
						<WhatIsThisContentContainer>
							<CategoryInfoContainer>
								<FormattedMessage
									{...description}
									values={{
										learnMoreLink: linkToItemDocumentation,
										br: <br />,
									}}
								/>
							</CategoryInfoContainer>
						</WhatIsThisContentContainer>
					</Box>
				)}
				trigger={(triggerProps) => (
					<Button
						appearance="subtle"
						isSelected={isOpen}
						id={buttonId}
						onClick={onToggle}
						iconBefore={<EditorPanelIcon LEGACY_size="medium" spacing="spacious" label="" />}
						aria-labelledby={`${buttonId} ${props.whatsThisLabelIdRef}`}
						aria-label={formatMessage(buttonLabel)}
						{...triggerProps}
					/>
				)}
			/>
		</Container>
	);
};

// We want to allow translated messages to be broken up across multiple lines
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled2.div<{
	children?: ReactNode;
}>({
	whiteSpace: 'pre-line',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CategoryInfoContainer = styled.div<{
	children?: ReactNode;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${gridSize * 36.25}px`,
	'@media (max-width: 1120px)': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		maxWidth: `${gridSize * 31}px`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WhatIsThisContentContainer = styled2.div<{
	children?: ReactNode;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${gridSize * 36.25}px`,
});

const contentStyles = xcss({
	padding: 'space.200',
});
